/**
 * SignalCard - Trading Signal Indicator
 *
 * Displays: Signal (BUY/SELL/HOLD), confidence, entry/SL/TP
 *
 * @version 1.0.0
 */

import React, { memo } from 'react';
import { Box, Typography } from '@mui/material';
import IndicatorCard from '../IndicatorCard';

const SignalCard = memo(({ strategySignal, signal, confidence }) => {
    const signalValue = signal || strategySignal?.signal || 'HOLD';
    const confidenceValue = confidence || strategySignal?.confidence || 0;
    const entry = strategySignal?.entry;
    const stopLoss = strategySignal?.stopLoss;
    const takeProfit = strategySignal?.takeProfit;

    const getSignalColor = (sig) => {
        if (!sig) return '#71717A';
        const s = sig.toUpperCase();
        if (s === 'BUY' || s === 'LONG') return '#22c55e';
        if (s === 'SELL' || s === 'SHORT') return '#ef4444';
        return '#fbbf24'; // HOLD/NEUTRAL
    };

    const getSignalStatus = (sig) => {
        if (!sig) return 'neutral';
        const s = sig.toUpperCase();
        if (s === 'BUY' || s === 'LONG') return 'bullish';
        if (s === 'SELL' || s === 'SHORT') return 'bearish';
        return 'neutral';
    };

    const signalColor = getSignalColor(signalValue);

    const summary = (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            {/* Signal */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: signalColor,
                        boxShadow: `0 0 8px ${signalColor}`
                    }}
                />
                <Typography
                    sx={{
                        fontSize: '14px',
                        fontWeight: 700,
                        color: signalColor,
                        textTransform: 'uppercase'
                    }}
                >
                    {signalValue}
                </Typography>
            </Box>

            {/* Confidence */}
            <Box
                sx={{
                    backgroundColor: `${signalColor}20`,
                    color: signalColor,
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontFamily: 'monospace'
                }}
            >
                {confidenceValue}%
            </Box>
        </Box>
    );

    return (
        <IndicatorCard
            title="SIGNAL"
            icon="🎯"
            summary={summary}
            badge={signalValue}
            badgeColor={signalColor}
            status={getSignalStatus(signalValue)}
            defaultExpanded={false}
        >
            {/* Expanded: Entry, SL, TP */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Entry */}
                {entry && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            backgroundColor: 'rgba(255, 255, 255, 0.03)',
                            borderRadius: '4px'
                        }}
                    >
                        <Typography sx={{ fontSize: '10px', color: '#A1A1AA' }}>
                            Entry
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: '#FAFAFA', fontWeight: 600, fontFamily: 'monospace' }}>
                            {entry.toFixed(2)}
                        </Typography>
                    </Box>
                )}

                {/* Stop Loss */}
                {stopLoss && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            backgroundColor: 'rgba(239, 68, 68, 0.05)',
                            borderRadius: '4px',
                            borderLeft: '2px solid #ef4444'
                        }}
                    >
                        <Typography sx={{ fontSize: '10px', color: '#ef4444' }}>
                            Stop Loss
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: '#ef4444', fontWeight: 600, fontFamily: 'monospace' }}>
                            {stopLoss.toFixed(2)}
                        </Typography>
                    </Box>
                )}

                {/* Take Profit */}
                {takeProfit && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            backgroundColor: 'rgba(34, 197, 94, 0.05)',
                            borderRadius: '4px',
                            borderLeft: '2px solid #22c55e'
                        }}
                    >
                        <Typography sx={{ fontSize: '10px', color: '#22c55e' }}>
                            Take Profit
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: '#22c55e', fontWeight: 600, fontFamily: 'monospace' }}>
                            {takeProfit.toFixed(2)}
                        </Typography>
                    </Box>
                )}

                {/* Confidence Bar */}
                <Box sx={{ mt: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '9px', color: '#71717A' }}>
                            Confidence
                        </Typography>
                        <Typography sx={{ fontSize: '9px', color: signalColor, fontWeight: 600 }}>
                            {confidenceValue}%
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            height: 4,
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: 2,
                            overflow: 'hidden'
                        }}
                    >
                        <Box
                            sx={{
                                width: `${confidenceValue}%`,
                                height: '100%',
                                backgroundColor: signalColor,
                                borderRadius: 2,
                                transition: 'width 0.3s ease'
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        </IndicatorCard>
    );
});

SignalCard.displayName = 'SignalCard';

export default SignalCard;
